import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React from "react";
import { MembershipService } from "src/app/services/Index";
import "./AdminMemberships.css";

type State = {
	available: boolean;
	loading: boolean;
	saving: boolean;
	products: any[];
	edit: any;
	/** The product whose holders are open, or the string "all". */
	membersFor: any;
	members: any[];
	membersLoading: boolean;
	includeExpired: boolean;
	search: string;
};

const money = (pence: any) => `£${((Number(pence) || 0) / 100).toFixed(2)}`;

const shortDate = (value: any) =>
	new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/** How near expiry counts as worth chasing. */
const RENEWAL_WINDOW_DAYS = 30;

const daysUntil = (value: any) =>
	Math.ceil((new Date(value).getTime() - Date.now()) / 86400000);

/**
 * Memberships the club sells, and who holds one.
 *
 * These are fixed-term and bought outright rather than recurring — the club
 * chases its own renewals, which is a decision made on the model. That makes
 * "who is about to lapse" the question this screen exists to answer, so expiry
 * is called out rather than left as a date to work out.
 */
export class AdminMemberships extends React.Component<{}, State> {
	private toast: Toast | null = null;

	constructor(props: {}) {
		super(props);
		this.state = {
			available: true, edit: null, includeExpired: false, loading: true,
			members: [], membersFor: null, membersLoading: false, products: [],
			saving: false, search: ""
		};
	}

	componentDidMount() {
		this.load();
	}

	async load() {
		try {
			const res = await MembershipService.getProducts();
			const body = res.ok ? await res.json() : { available: false, products: [] };
			this.setState({ available: body.available !== false, loading: false, products: body.products || [] });
		} catch {
			this.setState({ available: false, loading: false });
		}
	}

	blank() {
		return {
			capacity: null, description: "", durationMonths: 12, grantsPrioritySale: false,
			isPublished: false, name: "", pricePence: 0, sortOrder: 0
		};
	}

	async save() {
		const { edit } = this.state;
		if (!edit?.name?.trim()) {
			this.tell("error", "The membership needs a name.");
			return;
		}
		if (!edit.durationMonths || edit.durationMonths < 1) {
			this.tell("error", "How long does it run for?");
			return;
		}

		this.setState({ saving: true });
		try {
			const res = edit.id
				? await MembershipService.updateProduct(edit.id, edit)
				: await MembershipService.addProduct(edit);

			if (!res.ok) {
				this.tell("error", (await res.text()).replace(/^"|"$/g, "") || "Couldn't save that.");
				return;
			}
			this.setState({ edit: null });
			this.load();
		} catch {
			this.tell("error", "Couldn't save that.");
		} finally {
			this.setState({ saving: false });
		}
	}

	async remove(product: any) {
		// Says what it does and what it doesn't. Somebody who paid for twelve
		// months keeps them whether or not the club still sells it, and an
		// admin shouldn't have to guess that.
		const holders = product.members
			? ` The ${product.members} ${product.members === 1 ? "person who holds" : "people who hold"} one keep it until it runs out.`
			: "";

		// eslint-disable-next-line no-restricted-globals
		if (!window.confirm(`Stop selling ${product.name}?${holders}`)) return;

		try {
			await MembershipService.removeProduct(product.id);
			this.load();
		} catch {
			this.tell("error", "Couldn't remove that.");
		}
	}

	async openMembers(product: any) {
		this.setState({ members: [], membersFor: product, membersLoading: true, search: "" },
			() => this.loadMembers());
	}

	async loadMembers() {
		const { membersFor, includeExpired } = this.state;
		if (!membersFor) return;

		this.setState({ membersLoading: true });
		try {
			const params = new URLSearchParams();
			if (membersFor !== "all") params.set("productId", String(membersFor.id));
			if (includeExpired) params.set("includeExpired", "true");

			const res = await MembershipService.getMembers(params.toString());
			const body = res.ok ? await res.json() : { members: [] };
			this.setState({ members: body.members || [] });
		} catch {
			this.tell("error", "Couldn't load the members.");
		} finally {
			this.setState({ membersLoading: false });
		}
	}

	tell(severity: "success" | "error", detail: string) {
		this.toast?.show({ closable: false, detail, life: 4000, severity, summary: "Memberships" });
	}

	/** Holders matching the box, or all of them when it's empty. */
	visibleMembers() {
		const term = this.state.search.trim().toLowerCase();
		if (!term) return this.state.members;

		return this.state.members.filter((m: any) =>
			(m.name || "").toLowerCase().includes(term) || (m.email || "").toLowerCase().includes(term));
	}

	renderProduct(p: any) {
		const full = p.capacity != null && p.members >= p.capacity;

		return (
			<div className="ms-card" key={p.id}>
				<div className="ms-card-head">
					<div>
						<strong>{p.name}</strong>
						{!p.isPublished && <span className="ms-tag draft">DRAFT</span>}
						{p.grantsPrioritySale && <span className="ms-tag priority">PRIORITY SALE</span>}
						{full && <span className="ms-tag full">FULL</span>}
					</div>
					<span className="ms-price">{money(p.pricePence)}</span>
				</div>

				{p.description && <p className="ms-desc">{p.description}</p>}

				<div className="ms-meta">
					<span>{p.durationMonths} {p.durationMonths === 1 ? "month" : "months"}</span>
					<span>{p.members} {p.members === 1 ? "member" : "members"}</span>
					{p.capacity != null
						? <span>{Math.max(0, p.capacity - p.members)} of {p.capacity} left</span>
						: <span>No cap</span>}
				</div>

				<div className="ms-actions">
					<Button className="p-button-sm p-button-text" label="Edit" onClick={() => this.setState({ edit: { ...p } })} />
					<Button className="p-button-sm p-button-text" label="Members" onClick={() => this.openMembers(p)} />
					<Button className="p-button-sm p-button-text p-button-danger" label="Stop selling" onClick={() => this.remove(p)} />
				</div>
			</div>
		);
	}

	renderEditDialog() {
		const { edit } = this.state;
		if (!edit) return null;

		return (
			<Dialog footer={<>
				<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ edit: null })} />
				<Button disabled={this.state.saving} icon="pi pi-check"
					label={this.state.saving ? "Saving…" : "Save"} onClick={() => this.save()} />
			</>} header={edit.id ? "Edit membership" : "New membership"} modal
				onHide={() => this.setState({ edit: null })} style={{ maxWidth: "95vw", width: 520 }} visible>

				<label className="ms-label">Name</label>
				<InputText autoFocus onChange={(e) => this.setState({ edit: { ...edit, name: e.target.value } })}
					placeholder="Junior Iron" style={{ marginBottom: 14, width: "100%" }} value={edit.name || ""} />

				<label className="ms-label">What they get</label>
				<InputTextarea onChange={(e) => this.setState({ edit: { ...edit, description: e.target.value } })}
					rows={3} style={{ marginBottom: 14, width: "100%" }} value={edit.description || ""} />

				<label className="ms-label">Price</label>
				<InputNumber currency="GBP" locale="en-GB" mode="currency"
					onValueChange={(e) => this.setState({ edit: { ...edit, pricePence: Math.round((e.value || 0) * 100) } })}
					style={{ marginBottom: 14, width: "100%" }} value={(edit.pricePence || 0) / 100} />

				{/* Months rather than an end date: the term starts when somebody
				    buys, and every buyer starts on a different day. */}
				<label className="ms-label">How long it runs for (months)</label>
				<InputNumber min={1} onValueChange={(e) => this.setState({ edit: { ...edit, durationMonths: e.value } })}
					style={{ marginBottom: 14, width: "100%" }} value={edit.durationMonths} />

				<label className="ms-label">How many to sell</label>
				<InputNumber onValueChange={(e) => this.setState({ edit: { ...edit, capacity: e.value } })}
					placeholder="No limit" style={{ marginBottom: 16, width: "100%" }} value={edit.capacity} />

				<div className="ms-switch">
					<InputSwitch checked={!!edit.grantsPrioritySale}
						onChange={(e) => this.setState({ edit: { ...edit, grantsPrioritySale: e.value } })} />
					<div>
						<label>Gets priority booking</label>
						<small>Holders can buy in priority windows, same as a season ticket holder.</small>
					</div>
				</div>

				<div className="ms-switch">
					<InputSwitch checked={!!edit.isPublished}
						onChange={(e) => this.setState({ edit: { ...edit, isPublished: e.value } })} />
					<div>
						<label>On sale</label>
						<small>Off keeps it off the storefront while you set it up.</small>
					</div>
				</div>
			</Dialog>
		);
	}

	renderMembersDialog() {
		const { membersFor, membersLoading, includeExpired, search } = this.state;
		if (!membersFor) return null;

		const shown = this.visibleMembers();
		const all = membersFor === "all";

		return (
			<Dialog header={all ? "Everyone with a membership" : `${membersFor.name} — members`} modal
				onHide={() => this.setState({ membersFor: null })} style={{ maxWidth: "95vw", width: 680 }} visible>

				<div className="ms-filters">
					<InputText onChange={(e) => this.setState({ search: e.target.value })}
						placeholder="Search by name or email" value={search} />
					<label className="ms-check">
						<input checked={includeExpired} onChange={() => this.setState(
							{ includeExpired: !includeExpired }, () => this.loadMembers())} type="checkbox" />
						<span>Include lapsed</span>
					</label>
				</div>

				{membersLoading && <p className="ms-muted">Loading…</p>}

				{!membersLoading && !shown.length &&
					<p className="ms-muted">
						{search ? "Nobody matches that." : "Nobody holds one yet."}
					</p>}

				{!membersLoading && shown.length > 0 &&
					<table className="ms-table">
						<thead>
							<tr>
								<th>Name</th>
								{all && <th>Membership</th>}
								<th>Runs to</th>
							</tr>
						</thead>
						<tbody>
							{shown.map((m: any) => {
								const left = daysUntil(m.expiresAt);
								const soon = !m.expired && left <= RENEWAL_WINDOW_DAYS;

								return (
									<tr key={m.membershipId}>
										<td>
											<strong>{m.name || "Unnamed"}</strong>
											{m.email && <small>{m.email}</small>}
										</td>
										{all && <td>{m.product}</td>}
										<td>
											{shortDate(m.expiresAt)}
											{/* The club chases its own renewals, so the ones
											    worth chasing are called out here. */}
											{m.expired && <span className="ms-tag lapsed">LAPSED</span>}
											{soon && <span className="ms-tag soon">{left} {left === 1 ? "DAY" : "DAYS"} LEFT</span>}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>}

				{!membersLoading && shown.length >= 500 &&
					<p className="ms-muted ms-note">
						Showing the first 500. Narrow it down with the search box.
					</p>}
			</Dialog>
		);
	}

	render() {
		const { available, loading, products } = this.state;

		if (!loading && !available) {
			return <p className="ms-muted">Memberships aren&rsquo;t switched on for this club.</p>;
		}

		return (
			<div className="ms-page">
				<Toast ref={(el) => { this.toast = el; }} />

				<div className="ms-bar">
					<Button icon="pi pi-plus" label="New membership" onClick={() => this.setState({ edit: this.blank() })} />
					{products.length > 0 &&
						<Button className="p-button-text" icon="pi pi-users" label="All members"
							onClick={() => this.openMembers("all")} />}
				</div>

				{loading && <p className="ms-muted">Loading…</p>}

				{!loading && !products.length &&
					<div className="ms-empty">
						<h3>No memberships yet</h3>
						<p>A junior badge, a supporters&rsquo; club subscription &mdash; anything a fan holds for a season.</p>
					</div>}

				{products.map((p: any) => this.renderProduct(p))}

				{this.renderEditDialog()}
				{this.renderMembersDialog()}
			</div>
		);
	}
}
